import { from, of } from 'rxjs'
import { catchError, concatMap, filter, map, switchMap } from 'rxjs/operators'
import { errorNotification, successNotification, LoginBrandingService } from '~/Services'
import { ILoginCarouselItem } from '~/Services/LoginBranding.interface'
import { RootEpic } from '../types'
import { LoginBrandingActions } from './slice'

const getCarouselItemsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.getCarouselItems.match),
    switchMap(action => {
      const { applicationId } = action.payload
      return from(LoginBrandingService.Get.getAll(applicationId)).pipe(
        map(res => LoginBrandingActions.getCarouselItemsSuccess({ applicationId, items: res.data })),
        catchError(error => {
          errorNotification(error)
          return [LoginBrandingActions.getCarouselItemsFail()]
        }),
      )
    }),
  )

const createCarouselItemEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.createCarouselItem.match),
    switchMap(action => {
      const { applicationId, data, pendingImageFile } = action.payload
      return from(LoginBrandingService.Post.create(applicationId, data)).pipe(
        // Slide vừa tạo xong mới có itemId thật để gắn ảnh — nếu form "Add slide" đã chọn sẵn ảnh nền,
        // upload nối tiếp ngay ở đây thay vì bắt user quay lại bấm "Change" lần 2 sau khi Save.
        switchMap(createRes => {
          const createdItem = createRes.data as ILoginCarouselItem
          if (!pendingImageFile) {
            return of(createdItem)
          }

          return from(LoginBrandingService.UploadImage(applicationId, createdItem.id, pendingImageFile)).pipe(
            map(() => createdItem),
            catchError(uploadError => {
              // Slide đã tạo thành công — chỉ riêng bước upload ảnh lỗi thì báo riêng, không rollback/xem
              // như cả thao tác thất bại (item vẫn tồn tại, user có thể tải lại ảnh sau trong danh sách).
              errorNotification(uploadError, 'Đã thêm slide nhưng tải ảnh nền thất bại — có thể tải lại ảnh trong danh sách')
              return of(createdItem)
            }),
          )
        }),
        concatMap(() => {
          successNotification('Đã thêm slide!')
          return [
            LoginBrandingActions.createCarouselItemSuccess(),
            LoginBrandingActions.getCarouselItems({ applicationId }),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Thêm slide thất bại')
          return [LoginBrandingActions.createCarouselItemFail()]
        }),
      )
    }),
  )

const updateCarouselItemEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.updateCarouselItem.match),
    switchMap(action => {
      const { applicationId, itemId, data } = action.payload
      return from(LoginBrandingService.Put.update(applicationId, itemId, data)).pipe(
        concatMap(() => {
          successNotification('Đã cập nhật slide!')
          return [
            LoginBrandingActions.updateCarouselItemSuccess(),
            LoginBrandingActions.getCarouselItems({ applicationId }),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Cập nhật slide thất bại')
          return [LoginBrandingActions.updateCarouselItemFail()]
        }),
      )
    }),
  )

const deleteCarouselItemEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.deleteCarouselItem.match),
    switchMap(action => {
      const { applicationId, itemId } = action.payload
      return from(LoginBrandingService.Delete.delete(applicationId, itemId)).pipe(
        concatMap(() => {
          successNotification('Đã xoá slide!')
          return [
            LoginBrandingActions.deleteCarouselItemSuccess(),
            LoginBrandingActions.getCarouselItems({ applicationId }),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Xoá slide thất bại')
          return [LoginBrandingActions.deleteCarouselItemFail()]
        }),
      )
    }),
  )

const setCarouselItemActiveEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.setCarouselItemActive.match),
    switchMap(action => {
      const { applicationId, itemId, isActive } = action.payload
      return from(LoginBrandingService.Patch.setActive(applicationId, itemId, isActive)).pipe(
        concatMap(() => [
          LoginBrandingActions.itemMutationSettled(),
          LoginBrandingActions.getCarouselItems({ applicationId }),
        ]),
        catchError(error => {
          errorNotification(error, 'Cập nhật trạng thái slide thất bại')
          return [LoginBrandingActions.itemMutationSettled()]
        }),
      )
    }),
  )

const setCarouselItemDisplayOrderEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.setCarouselItemDisplayOrder.match),
    switchMap(action => {
      const { applicationId, itemId, displayOrder } = action.payload
      return from(LoginBrandingService.Patch.setDisplayOrder(applicationId, itemId, displayOrder)).pipe(
        concatMap(() => [
          LoginBrandingActions.itemMutationSettled(),
          LoginBrandingActions.getCarouselItems({ applicationId }),
        ]),
        catchError(error => {
          errorNotification(error, 'Cập nhật thứ tự slide thất bại')
          return [LoginBrandingActions.itemMutationSettled()]
        }),
      )
    }),
  )

const uploadCarouselItemImageEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.uploadCarouselItemImage.match),
    switchMap(action => {
      const { applicationId, itemId, file } = action.payload
      return from(LoginBrandingService.UploadImage(applicationId, itemId, file)).pipe(
        concatMap(() => {
          successNotification('Đã cập nhật ảnh nền!')
          return [
            LoginBrandingActions.itemMutationSettled(),
            LoginBrandingActions.getCarouselItems({ applicationId }),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Cập nhật ảnh nền thất bại')
          return [LoginBrandingActions.itemMutationSettled()]
        }),
      )
    }),
  )

const removeCarouselItemImageEpic: RootEpic = action$ =>
  action$.pipe(
    filter(LoginBrandingActions.removeCarouselItemImage.match),
    switchMap(action => {
      const { applicationId, itemId } = action.payload
      return from(LoginBrandingService.Delete.deleteImage(applicationId, itemId)).pipe(
        concatMap(() => {
          successNotification('Đã gỡ ảnh nền!')
          return [
            LoginBrandingActions.itemMutationSettled(),
            LoginBrandingActions.getCarouselItems({ applicationId }),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Gỡ ảnh nền thất bại')
          return [LoginBrandingActions.itemMutationSettled()]
        }),
      )
    }),
  )

export const LoginBrandingEpics = [
  getCarouselItemsEpic,
  createCarouselItemEpic,
  updateCarouselItemEpic,
  deleteCarouselItemEpic,
  setCarouselItemActiveEpic,
  setCarouselItemDisplayOrderEpic,
  uploadCarouselItemImageEpic,
  removeCarouselItemImageEpic,
]
